import { z } from 'zod';
import { LocalizedText } from './common';
import { LOCALES, LocationId } from './enums';

/**
 * Live app config: what the team can change from the back office without shipping a new store
 * build. The app ships DEFAULT_APP_CONFIG and overlays whatever GET /app/config returns, so every
 * field must have a safe default and the app must work offline with none of it.
 *
 * Images are stored as asset keys (`assets/bg/cafe/evening.webp`); the server turns them into URLs
 * before sending. A value that already starts with http(s) is sent as is.
 */

/** The five bottom tabs. Routes are fixed in the app; config can only reorder or hide them. */
export const TAB_KEYS = ['index', 'explore', 'people', 'manga', 'me'] as const;
export const TabKey = z.enum(TAB_KEYS);
export type TabKey = z.infer<typeof TabKey>;

/** Home (今日) blocks the app knows how to draw. Unknown keys are ignored by older apps. */
export const HOME_SECTIONS = ['next', 'recap', 'agenda', 'featured', 'goals', 'invites'] as const;
export const HomeSection = z.enum(HOME_SECTIONS);
export type HomeSection = z.infer<typeof HomeSection>;
/** The default order. Invites live in 人物 → 訊息; config can bring them back to 今日. */
export const DEFAULT_HOME_SECTIONS: HomeSection[] = ['next', 'recap', 'agenda', 'featured', 'goals'];

/** Pages with a full-bleed hero whose art is picked by config (tabs whose hero is a person use live data instead). */
export const HERO_KEYS = ['explore', 'manga', 'store', 'plus', 'settings', 'welcome', 'city'] as const;
export const HeroKey = z.enum(HERO_KEYS);
export type HeroKey = z.infer<typeof HeroKey>;

const unit = z.number().min(0).max(1);

export const HeroArt = z.object({
  image: z.string().max(300).nullable().default(null),
  /** Vertical focus of the art (0 = top, 1 = bottom), for `contentPosition`. */
  focusY: unit.default(0.35),
});
export type HeroArt = z.infer<typeof HeroArt>;

export const MapPin = z.object({ locationId: LocationId, x: unit, y: unit });
export type MapPin = z.infer<typeof MapPin>;

/** A live-ops notice shown at the top of 今日 (an event, a maintenance window, a new chapter). */
export const Notice = z.object({
  id: z.string().min(1).max(40),
  title: LocalizedText,
  body: LocalizedText.optional(),
  image: z.string().max(300).nullable().default(null),
  /** In-app route to open on tap (e.g. `/store`); omitted means the notice is not tappable. */
  href: z.string().max(200).regex(/^\//).optional(),
  startsAt: z.string().datetime().optional(),
  endsAt: z.string().datetime().optional(),
});
export type Notice = z.infer<typeof Notice>;

/** The painted town map and where each place sits on it (fractions of the hero box). */
const DEFAULT_EXPLORE = {
  map: 'assets/ui/town_map.webp',
  pins: [
    { locationId: 'university', x: 0.64, y: 0.17 },
    { locationId: 'park', x: 0.6, y: 0.36 },
    { locationId: 'cafe', x: 0.4, y: 0.49 },
    { locationId: 'town_square', x: 0.06, y: 0.55 },
    { locationId: 'apartment', x: 0.5, y: 0.63 },
    { locationId: 'seaside', x: 0.04, y: 0.21 },
  ],
} satisfies { map: string; pins: MapPin[] };

/**
 * The 墨染 backdrops: collages of manga pages with ink splats, sakura splashes and a lime stripe.
 * Every page about a character stands them in front of one; the page picks one by the character.
 */
const DEFAULT_BACKDROPS = ['assets/ui/collage_a.webp', 'assets/ui/collage_b.webp', 'assets/ui/collage_c.webp'];

/** Inked Plus opens on the cast key art until the back office picks something else. */
const DEFAULT_HEROES: Partial<Record<HeroKey, HeroArt>> = {
  plus: { image: 'assets/ui/keyart.webp', focusY: 0.3 },
};

export const AppConfig = z.object({
  /** Bumped on every save, so the app can tell a fresh copy from its cache. */
  revision: z.number().int().min(0).default(0),
  tabs: z.array(z.object({ key: TabKey, hidden: z.boolean().default(false) })).default(TAB_KEYS.map((key) => ({ key, hidden: false }))),
  home: z.object({ sections: z.array(HomeSection).default(DEFAULT_HOME_SECTIONS) }).default({ sections: DEFAULT_HOME_SECTIONS }),
  heroes: z.partialRecord(HeroKey, HeroArt).default(DEFAULT_HEROES),
  explore: z
    .object({ map: z.string().max(300).nullable().default(null), pins: z.array(MapPin).default([]) })
    .default(DEFAULT_EXPLORE),
  backdrops: z.array(z.string().max(300)).max(12).default(DEFAULT_BACKDROPS),
  notices: z.array(Notice).max(5).default([]),
  /** Copy overrides by locale and i18n key: fix a typo or retitle a section without a release. */
  copy: z.partialRecord(z.enum(LOCALES), z.record(z.string().max(120), z.string().max(2000))).default({}),
  /**
   * Whether the website links to its shop (nav, home and feature pages, account). Off: the shop still
   * opens for players the app sends there, but a visitor finds no way in.
   */
  webShopEntry: z.boolean().default(false),
  /** Switches for whole features. Voice lines are parked until the voice redesign. */
  features: z.object({ voice: z.boolean().default(false) }).default({ voice: false }),
  /** Below this app version the app shows a blocking "please update" screen. */
  minAppVersion: z.string().regex(/^\d+\.\d+\.\d+$/).nullable().default(null),
});
export type AppConfig = z.infer<typeof AppConfig>;

/** What the app uses before (or without) the server's copy. */
export const DEFAULT_APP_CONFIG: AppConfig = AppConfig.parse({});
